import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Bento layout primitives for the two portals.
 *
 * A bento is a grid of cells of deliberately unequal size, where the size IS the
 * ranking: the thing a resident opens the portal to find out — what do I owe — gets
 * the biggest cell and the biggest number, and everything else is arranged around
 * it. That only works if the sizes are assigned by importance and not by how much
 * text happens to be in each card, which is why span is a prop with four values
 * rather than a free-form style.
 *
 * ---------------------------------------------------------------------------
 * Two rules borrowed from charting practice, because they apply to stat tiles too
 * ---------------------------------------------------------------------------
 *
 * 1. EXACTLY ONE hero figure per view. Two 48px numbers is two headlines, which is
 *    none. Everything else is a stat tile at body scale.
 *
 * 2. Proportional figures for standalone numbers, tabular ONLY in columns.
 *    `tabular-nums` gives every digit the width of a zero, which keeps a table
 *    column aligned but makes a large standalone number look gappy. So the hero and
 *    the tile values use the font's default figures, and `.table .num` opts into
 *    tabular. See styles.css.
 *
 * Both portals are read-only, so there are no actions in these cells and no
 * hover state beyond a link — a cell that looks pressable and is not is a worse
 * affordance than a plain card.
 */

type Span = 'sm' | 'md' | 'wide' | 'full';

export function Bento({ children }: { children: ReactNode }) {
  return <div className="bento">{children}</div>;
}

/**
 * One cell.
 *
 * `tone` is for the one case where a cell carries state rather than data — an
 * overdue balance. It is never colour alone: the cell also says "past due" in words,
 * because a red card is invisible to a reader who cannot see red and meaningless to
 * one who has not learned the convention.
 */
export function BentoCell({
  span = 'md',
  title,
  hint,
  tone,
  children,
}: {
  span?: Span;
  title?: string;
  hint?: string;
  tone?: 'default' | 'attention';
  children: ReactNode;
}) {
  return (
    <section
      className={[
        'bento__cell',
        `bento__cell--${span}`,
        tone === 'attention' ? 'bento__cell--attention' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Sentence case, no trailing colon — a tile label is a caption, not a form
          field. The heading level is h2 throughout: these are siblings under the
          page h1, not a hierarchy. */}
      {title ? <h2 className="bento__title">{title}</h2> : null}
      {hint ? <p className="bento__hint">{hint}</p> : null}
      {children}
    </section>
  );
}

/**
 * The one number the page leads with.
 *
 * `value` is a ReactNode and never a number, because on both portals it is usually
 * money — a decimal string straight from the server that must not be parsed
 * (invariant 2). Nothing in this component formats, rounds, or sums anything.
 */
export function HeroFigure({
  value,
  label,
  caption,
  tone = 'default',
}: {
  value: ReactNode;
  label: string;
  caption?: ReactNode;
  /**
   * 'pending'/'due' swap the default gradient-clip figure for a flat,
   * solid-colored one (see the matching `.hero--*` rules in styles.css) —
   * first use: the tenant homepage's payment-pending (orange) and
   * full-balance-due (red) states, where the color itself is the signal
   * and can't be left to however a given browser renders the gradient.
   */
  tone?: 'default' | 'pending' | 'due';
}) {
  return (
    <div className={tone === 'default' ? 'hero' : `hero hero--${tone}`}>
      <span className="hero__label">{label}</span>
      <span className="hero__value">{value}</span>
      {caption ? <span className="hero__caption">{caption}</span> : null}
    </div>
  );
}

/** A label/value pair inside a cell. The workhorse of both portals. */
export function BentoStat({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  to?: string;
}) {
  const body = (
    <>
      <span className="bento__value">{value}</span>
      <span className="bento__label">{label}</span>
      {hint ? <span className="bento__sub">{hint}</span> : null}
    </>
  );

  return to ? (
    <Link className="bento__stat bento__stat--link" to={to}>
      {body}
    </Link>
  ) : (
    <div className="bento__stat">{body}</div>
  );
}
